import type { UseQueryResult } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import { Inbox, Loader2, RefreshCw, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/cn';
import { Button } from './Button';

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-subtle', className)}>
      <Loader2 aria-hidden className="size-5 animate-spin" />
      {label ? <span className="text-sm">{label}</span> : <span className="sr-only">…</span>}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-shimmer rounded-sm bg-track', className)} />;
}

/** Placeholder rows shaped like a table. */
export function SkeletonRows({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="flex flex-col" role="status" aria-busy="true">
      <span className="sr-only">…</span>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 border-b border-line-faint px-4 py-4">
          {Array.from({ length: columns }, (_cell, c) => (
            <Skeleton key={c} className={cn('h-3.5', c === 0 ? 'w-1/4' : 'flex-1')} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div className={cn('grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4', className)} role="status" aria-busy="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-5 py-4">
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  body,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-12 text-center', className)}>
      <span className="mb-1 grid size-12 place-items-center rounded-full bg-track text-subtle">
        <Icon aria-hidden className="size-6" />
      </span>
      <p className="text-md font-semibold">{title}</p>
      {body ? <p className="max-w-md text-base text-subtle">{body}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry, className, compact }: { error: unknown; onRetry?: () => void; className?: string; compact?: boolean }) {
  const { t, errorText } = useI18n();
  return (
    <div role="alert" className={cn('flex flex-col items-center gap-2 px-6 text-center', compact ? 'py-6' : 'py-12', className)}>
      <span className="mb-1 grid size-12 place-items-center rounded-full bg-danger-tint text-danger-text">
        <TriangleAlert aria-hidden className="size-6" />
      </span>
      <p className="text-md font-semibold">{t('error.pageTitle')}</p>
      <p className="max-w-md text-base text-subtle">{errorText(error)}</p>
      {onRetry ? (
        <Button icon={RefreshCw} onClick={onRetry} className="mt-2">
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Loading → skeleton, error → retry, empty → empty state, else render data.
 * Keeps every screen's three states consistent.
 */
export function QueryState<T>({
  query,
  skeleton,
  empty,
  isEmpty,
  children,
}: {
  query: UseQueryResult<T>;
  skeleton: ReactNode;
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <>{skeleton}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (empty && isEmpty?.(query.data)) return <>{empty}</>;
  return <>{children(query.data)}</>;
}
